import { Controller } from "@hotwired/stimulus";
import type { ExportAssets } from "../maps/export";
import { ExportCancelled, type ExportSnapshot, exportFileName, exportSize, exportSnapshot } from "../maps/export_rules";
import { anyChecked, type Check, type Checks, exportUrl, fill, footer, noChecks, revealList, unavailable, verdict } from "../maps/export_options";
import { releasePictures } from "../maps/picture_loader";
import type { LiveScene } from "../types/generated/live";
import type MapBoard from "./map_board_controller";

type Labels = {
	size: string;
	capped: { square: string; hex: string };
	fallbacks: { one: string; other: string };
	copied: string;
	copyFailed: string;
	shareFailed: string;
	shareRetry: string;
	preview: string;
	reveals: Record<Check, string>;
};
type Raw = ExportSnapshot & { scene: LiveScene };
type Picture = ReturnType<typeof exportSnapshot<Raw>>;
// The renderer is a chunk of its own that the board loads late; the dialog asks for it only once something is to be drawn.
const renderPicture = async (options: Parameters<typeof import("../maps/export").renderExport>[0]) => (await import("../maps/export")).renderExport(options);
type Phase = "loading" | "ready" | "failed";

// The preview's box before the layout can say; the dialog sizes it at 3:2.
const PREVIEW = { width: 450, height: 300 };
const COPIED_MS = 2000;
// A phone shares from the click, so the picture is drawn once the master stops changing checks.
const WARM_MS = 600;

// Connects to data-controller="map-export", on the board root beside map-board (docs/architecture/maps.md, "Exportar imagem").
// A player's picture is the snapshot the board already holds; a master's is fetched from the server with the checks as params.
export default class extends Controller<HTMLElement> {
	static targets = [
		"dialog",
		"stage",
		"loading",
		"failure",
		"fileName",
		"size",
		"capped",
		"fallbacks",
		"check",
		"pieces",
		"description",
		"reason",
		"verdict",
		"reveals",
		"button",
		"notice",
	];
	static values = { url: String, labels: Object };
	declare readonly dialogTarget: HTMLDialogElement;
	declare readonly stageTarget: HTMLElement;
	declare readonly loadingTarget: HTMLElement;
	declare readonly failureTarget: HTMLElement;
	declare readonly fileNameTarget: HTMLElement;
	declare readonly sizeTarget: HTMLElement;
	declare readonly cappedTarget: HTMLElement;
	declare readonly fallbacksTarget: HTMLElement;
	declare readonly checkTargets: HTMLInputElement[];
	declare readonly piecesTarget: HTMLInputElement;
	declare readonly descriptionTargets: HTMLElement[];
	declare readonly reasonTargets: HTMLElement[];
	declare readonly verdictTargets: HTMLElement[];
	declare readonly hasRevealsTarget: boolean;
	declare readonly revealsTarget: HTMLElement;
	declare readonly buttonTargets: HTMLButtonElement[];
	declare readonly noticeTarget: HTMLElement;
	declare readonly urlValue: string;
	declare readonly labelsValue: Labels;
	phase: Phase = "loading";
	// Bumped by every build, so an answer that arrives after a newer request is dropped.
	run = 0;
	fetching = false;
	abort?: AbortController;
	raw?: Raw;
	picture?: Picture;
	blob?: Promise<Blob>;
	// Whether the file for the picture on show is made, which is what lets Compartilhar start the share inside the tap.
	shareReady = false;
	downloads: string[] = [];
	warming?: ReturnType<typeof setTimeout>;
	restoring?: ReturnType<typeof setTimeout>;
	teardown?: ReturnType<typeof setTimeout>;
	// The copy button's own label, kept while "Imagem copiada" stands in for it.
	copyText?: string | null;

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard;
	}

	get master() {
		return this.board.manageValue;
	}

	get assets(): ExportAssets {
		const board = this.board;
		return {
			tiles: board.tilesValue,
			monoTiles: board.monoTilesValue,
			rock: board.rockValue,
			frameless: board.framelessValue,
			monoPlaces: board.monoPlacesValue,
		};
	}

	get checks(): Checks {
		const checks = noChecks();
		for (const input of this.checkTargets) checks[input.dataset.check as Check] = input.checked;
		return checks;
	}

	get view() {
		return this.board.state.view.peek() as unknown as Raw;
	}

	get sharing() {
		return !this.buttonFor("share").hidden;
	}

	buttonFor(kind: string) {
		return this.buttonTargets.find((button) => button.dataset.kind === kind) as HTMLButtonElement;
	}

	// Turbo moves the permanent board root, which disconnects and reconnects this controller: the dialog is only torn down once the
	// element has really left the page, as the board does.
	connect() {
		clearTimeout(this.teardown);
	}

	disconnect() {
		this.teardown = setTimeout(() => {
			if (this.element.isConnected) return;
			clearTimeout(this.restoring);
			this.closed();
		});
	}

	// Every opening starts from the picture that is safe to post: no check on, the pieces in.
	open() {
		for (const input of this.checkTargets) input.checked = false;
		this.piecesTarget.checked = true;
		this.noticeTarget.textContent = "";
		this.markUnavailable();
		this.layoutFooter();
		this.dialogTarget.showModal();
		this.load();
	}

	close() {
		this.dialogTarget.close();
	}

	// Also runs for Escape, which closes the dialog without `close`.
	closed() {
		this.run++;
		this.fetching = false;
		this.abort?.abort();
		clearTimeout(this.warming);
		this.discard();
		this.stageTarget.querySelector("canvas")?.remove();
		for (const url of this.downloads) URL.revokeObjectURL(url);
		this.downloads = [];
		releasePictures();
	}

	backdropClose(event: Event) {
		if (event.target === this.dialogTarget) this.close();
	}

	changed() {
		this.load();
	}

	// A fetch still out reads the box when it lands, so only a picture already drawn needs drawing again; a failed dialog asks again.
	piecesChanged() {
		if (this.phase === "failed") this.load();
		else if (this.raw && !this.fetching) this.draw(this.begin());
	}

	retry() {
		this.load();
	}

	// A check that would change nothing is disabled, and its description gives way to why.
	markUnavailable() {
		const none = this.master ? unavailable(this.view) : [];
		for (const input of this.checkTargets) input.disabled = none.includes(input.dataset.check as Check);
		for (const part of this.descriptionTargets) part.hidden = none.includes(part.dataset.check as Check);
		for (const part of this.reasonTargets) part.hidden = !none.includes(part.dataset.check as Check);
	}

	layoutFooter() {
		const coarse = matchMedia("(pointer: coarse)").matches;
		const canShare = typeof File === "function" && Boolean(navigator.canShare?.({ files: [new File([], "mapa.png", { type: "image/png" })] }));
		const shown = footer({ coarse, canShare, canCopy: typeof ClipboardItem === "function" && Boolean(navigator.clipboard?.write) });
		for (const kind of ["share", "copy", "download"] as const) this.buttonFor(kind).hidden = !shown[kind];
	}

	// The snapshot is the server's for a master, with the checks that are on, and the board's own for a player.
	async load() {
		this.abort?.abort();
		this.abort = new AbortController();
		const run = this.begin();
		this.showVerdict();
		this.facts();
		try {
			if (this.master) {
				// Nothing is drawable until the answer for these checks arrives.
				this.raw = undefined;
				this.fetching = true;
				const response = await fetch(exportUrl(this.urlValue, this.checks), {
					signal: this.abort.signal,
					headers: { Accept: "application/json" },
					credentials: "same-origin",
				});
				if (!response.ok) throw new Error(`Export answered ${response.status}`);
				const raw = (await response.json()) as Raw;
				if (run !== this.run) return;
				this.raw = raw;
			} else {
				this.raw = this.view;
			}
			this.fetching = false;
			await this.draw(run);
		} catch (error) {
			if (run === this.run && (error as Error).name !== "AbortError") this.fail();
		}
	}

	begin() {
		this.run++;
		clearTimeout(this.warming);
		this.setPhase("loading");
		return this.run;
	}

	// Draws the preview from the snapshot kept, with the pieces in or out.
	async draw(run: number) {
		try {
			const picture = exportSnapshot(this.raw as Raw, { pieces: this.piecesTarget.checked });
			const box = this.stageTarget.getBoundingClientRect();
			const crisp = Math.min(window.devicePixelRatio || 1, 2);
			const scale = Math.min((box.width || PREVIEW.width) / picture.scene.width_px, (box.height || PREVIEW.height) / picture.scene.height_px, 1) * crisp;
			const { canvas, fallbacks } = await renderPicture({ snapshot: picture, assets: this.assets, scale, stale: () => run !== this.run });
			if (run !== this.run) {
				canvas.width = 0;
				canvas.height = 0;
				return;
			}
			this.discard();
			this.picture = picture;
			canvas.className = "max-w-full max-h-full";
			canvas.setAttribute("role", "img");
			canvas.setAttribute("aria-label", this.labelsValue.preview);
			this.stageTarget.querySelector("canvas")?.remove();
			this.stageTarget.prepend(canvas);
			this.facts();
			this.showFallbacks(fallbacks);
			this.setPhase("ready");
			if (this.sharing) this.warm();
		} catch (error) {
			if (run === this.run && !(error instanceof ExportCancelled)) this.fail();
		}
	}

	setPhase(phase: Phase) {
		this.phase = phase;
		this.loadingTarget.hidden = phase !== "loading";
		this.failureTarget.hidden = phase !== "failed";
		for (const button of this.buttonTargets) button.disabled = phase !== "ready" || (button.dataset.kind === "share" && !this.shareReady);
		if (phase !== "ready") this.noticeTarget.textContent = "";
	}

	fail() {
		this.fetching = false;
		this.setPhase("failed");
	}

	// The picture and its file are made again for the next snapshot.
	discard() {
		this.showFallbacks(0);
		this.picture = undefined;
		this.blob = undefined;
		this.shareReady = false;
	}

	showVerdict() {
		if (!this.master) return;
		const checks = this.checks;
		const kind = verdict(checks, Boolean(this.view.scene.active));
		for (const part of this.verdictTargets) part.hidden = part.dataset.verdict !== kind;
		if (this.hasRevealsTarget) this.revealsTarget.textContent = revealList(checks, this.labelsValue.reveals, document.documentElement.lang || "pt-BR");
	}

	// The file's name and size, and the per-square size when the cap lowered it.
	facts() {
		const scene = this.view.scene;
		this.fileNameTarget.textContent = exportFileName(scene.name, this.master && anyChecked(this.checks));
		const shown = this.picture?.scene ?? scene;
		const { width, height, scale } = exportSize(shown);
		this.sizeTarget.textContent = fill(this.labelsValue.size, { width, height });
		const lowered = scale < 1;
		this.cappedTarget.hidden = !lowered;
		const side = scene.grid.cell_w;
		this.cappedTarget.textContent = lowered
			? fill(scene.grid.kind === "hex" ? this.labelsValue.capped.hex : this.labelsValue.capped.square, {
					scaled: Math.round(side * scale),
					full: Math.round(side),
				})
			: "";
	}

	// How many portraits came out as initials, in the picture on show; the file's own count replaces the preview's, as it is the one downloaded.
	showFallbacks(count: number) {
		const { one, other } = this.labelsValue.fallbacks;
		this.fallbacksTarget.hidden = count === 0;
		this.fallbacksTarget.textContent = count === 0 ? "" : fill(count === 1 ? one : other, { count });
	}

	// The file at the scale that fits the cap, made once for the picture on show. A failure to draw it is the dialog's failure, never a download without the map.
	file(): Promise<Blob> {
		if (this.blob) return this.blob;
		const picture = this.picture as Picture;
		const run = this.run;
		const stale = () => this.picture !== picture || this.run !== run;
		const made: Promise<Blob> = renderPicture({ snapshot: picture, assets: this.assets, scale: exportSize(picture.scene).scale, stale })
			.then(async ({ canvas, fallbacks }) => {
				if (this.picture === picture) this.showFallbacks(fallbacks);
				try {
					const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((png) => (png ? resolve(png) : reject(new Error("No PNG"))), "image/png"));
					// The render's own checks passed before the encoding, which a picture replaced meanwhile must not outlive.
					if (stale()) throw new ExportCancelled();
					return blob;
				} finally {
					canvas.width = 0;
					canvas.height = 0;
				}
			})
			.catch((error) => {
				if (this.blob === made) {
					this.blob = undefined;
					if (!(error instanceof ExportCancelled)) this.fail();
				}
				throw error;
			});
		this.blob = made;
		return made;
	}

	// Compartilhar stays disabled until the file is made, so the share starts inside the tap.
	warm() {
		clearTimeout(this.warming);
		const picture = this.picture;
		this.warming = setTimeout(() => {
			this.file()
				.then(() => {
					if (this.picture !== picture) return;
					this.shareReady = true;
					this.setPhase(this.phase);
				})
				.catch(() => {});
		}, WARM_MS);
	}

	get fileName() {
		return this.fileNameTarget.textContent as string;
	}

	// Called in the click itself, with the file as a promise, so Safari keeps the gesture while it is drawn.
	copy() {
		if (this.phase !== "ready") return;
		const label = this.buttonFor("copy").querySelector("[data-part=label]") as HTMLElement;
		this.copyText ??= label.textContent;
		const run = this.run;
		const done = () => {
			if (run !== this.run) return;
			label.textContent = this.labelsValue.copied;
			clearTimeout(this.restoring);
			this.restoring = setTimeout(() => (label.textContent = this.copyText as string), COPIED_MS);
		};
		const refuse = () => {
			if (run === this.run && this.phase === "ready") this.noticeTarget.textContent = this.labelsValue.copyFailed;
		};
		try {
			navigator.clipboard.write([new ClipboardItem({ "image/png": this.file() })]).then(done, refuse);
		} catch {
			refuse();
		}
	}

	async download() {
		if (this.phase !== "ready") return;
		const run = this.run;
		const blob = await this.file().catch(() => null);
		if (!blob || this.run !== run) return;
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = this.fileName;
		document.body.append(link);
		link.click();
		link.remove();
		this.downloads.push(link.href);
	}

	async share() {
		if (this.phase !== "ready") return;
		const blob = await this.file().catch(() => null);
		if (!blob) return;
		try {
			await navigator.share({ files: [new File([blob], this.fileName, { type: "image/png" })] });
		} catch (error) {
			const name = (error as Error).name;
			if (name === "NotAllowedError") this.noticeTarget.textContent = this.labelsValue.shareRetry;
			else if (name !== "AbortError") this.noticeTarget.textContent = this.labelsValue.shareFailed;
		}
	}
}
